import {
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import {
    createUserAction,
    editUserAction,
} from "../../Actions/UserActions";
// import VisibilityIcon from "@mui/icons-material/Visibility";
// import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
// import InfoIcon from "@mui/icons-material/Info";
import { SelectDropdown_SX } from "../../Assets/customTheme/constants";
import { CustomSwitch } from "../../Assets/customTheme/customswitch";
import { MEDIA_URL, REACT_APP_PROFILE_FOLDER_NAME } from "../../config";
import ProfilePictureUpload from "../../components/Common/ProfilePictureUpload";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

const validationCreateSchema = yup.object({
    firstName: yup.string().trim().max(50, VALIDATION.MAX_LENGTH("First Name", 50)).matches(/^[a-zA-Z_\-'''`–—]+$/, VALIDATION.INVALID_FORMAT("First Name")).required(VALIDATION.REQUIRED_FIELD("First Name")),
    lastName: yup.string().trim().max(50, VALIDATION.MAX_LENGTH("Last Name", 50)).matches(/^[a-zA-Z_\-'''`–—]+$/, VALIDATION.INVALID_FORMAT("Last Name")).required(VALIDATION.REQUIRED_FIELD("Last Name")),
    email: yup
        .string()
        .email(VALIDATION.INVALID_EMAIL)
        .max(70, VALIDATION.MAX_LENGTH("Email", 70))
        .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            VALIDATION.INVALID_EMAIL,
        )
        .required(VALIDATION.REQUIRED_FIELD("Email")),
    // password: yup
    //     .string()
    //     .trim()
    //     .required("Please enter the Password.")
    //     .matches(
    //         /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()-_+=]).{8,}$/,
    //         "Password must follow the criteria"
    //     ),
    profilePic: yup
        .mixed()
        .nullable()
        .test(
            "fileType",
            VALIDATION.FILE_TYPE_INVALID(['jpeg', 'png', 'jpg']),
            (value) => {
                if (!value) {
                    return true; // Allow null/empty
                }

                const isFile = value instanceof File;
                if (!isFile) {
                    return true; // Allow existing string URLs
                }
                if (value) {
                    const supportedFormats = ["jpeg", "png", "jpg"];
                    return supportedFormats.includes(
                        value.name.split(".").pop(),
                    );
                }
                return true;
            },
        )
        .test("fileSize", VALIDATION.FILE_SIZE_MAX("2 MB", "Profile picture"), (value) => {
            const maxFileSize = 2 * 1024 * 1024; // 2 MB
            if (!value) {
                return true;
            }

            const isFile = value instanceof File;
            if (!isFile) {
                return true;
            }

            return value.size <= maxFileSize;
        }),
    roleId: yup
        .number()
        .required(VALIDATION.SELECT_OPTION("Role"))
        .positive(VALIDATION.INVALID_FORMAT("Role"))
        .integer(VALIDATION.INVALID_FORMAT("Role")), // Validation for at least one selection
    assignUserIds: yup
        .string()
        .required(VALIDATION.SELECT_OPTION("Tenant User"))
        .test('not-empty', VALIDATION.SELECT_OPTION("Tenant User"), (value) => {
            return value && value.trim().length > 0;
        }),
});

const validationUpdateSchema = yup.object({
    firstName: yup.string().max(50, VALIDATION.MAX_LENGTH("First Name", 50)).matches(/^[a-zA-Z_\-'''`–—]+$/, VALIDATION.INVALID_FORMAT("First Name")).required(VALIDATION.REQUIRED_FIELD("First Name")),
    lastName: yup.string().max(50, VALIDATION.MAX_LENGTH("Last Name", 50)).matches(/^[a-zA-Z_\-'''`–—]+$/, VALIDATION.INVALID_FORMAT("Last Name")).required(VALIDATION.REQUIRED_FIELD("Last Name")),
    email: yup
        .string()
        .email(VALIDATION.INVALID_EMAIL)
        .max(70, VALIDATION.MAX_LENGTH("Email", 70))
        .required(VALIDATION.REQUIRED_FIELD("Email")),
    profilePic: yup
        .mixed()
        .nullable()
        .test(
            "fileType",
            VALIDATION.FILE_TYPE_INVALID(['jpeg', 'png', 'jpg']),
            (value) => {
                if (!value) {
                    return true; // Allow null/empty
                }

                const isFile = value instanceof File;
                if (!isFile) {
                    return true; // Allow existing string URLs
                }
                if (value) {
                    const supportedFormats = ["jpeg", "png", "jpg"];
                    return supportedFormats.includes(
                        value.name.split(".").pop(),
                    );
                }
                return true;
            },
        )
        .test("fileSize", VALIDATION.FILE_SIZE_MAX("2 MB", "Profile picture"), (value) => {
            const maxFileSize = 2 * 1024 * 1024; // 2 MB
            if (!value) {
                return true;
            }

            const isFile = value instanceof File;
            if (!isFile) {
                return true;
            }

            return value.size <= maxFileSize;
        }),
    roleId: yup
        .number()
        .required(VALIDATION.SELECT_OPTION("Role"))
        .positive(VALIDATION.INVALID_FORMAT("Role"))
        .integer(VALIDATION.INVALID_FORMAT("Role")),
    assignUserIds: yup
        .string()
        .required(VALIDATION.SELECT_OPTION("Tenant User"))
        .test('not-empty', VALIDATION.SELECT_OPTION("Tenant User"), (value) => {
            return value && value.trim().length > 0;
        }),
});

const AddEditMasterUserModal = ({
    open,
    handleClose,
    masterUserDataById,
    setSnackbar,
    roles,
    merchantUser,
    setShouldFetchData,
}) => {
    const [isImageEdited, setIsImageEdited] = useState(false);
    const [buttonDisable, setButtonDisable] = useState(false);
    const [imageRemoved, setImageRemoved] = useState(false);
    const [loading, setLoading] = useState(false);
    const profileFolderName = REACT_APP_PROFILE_FOLDER_NAME || "profilePics";

    const formik = useFormik({
        initialValues: {
            firstName: masterUserDataById?.firstName
                ? masterUserDataById.firstName
                : "",
            lastName: masterUserDataById?.lastName
                ? masterUserDataById.lastName
                : "",
            email: masterUserDataById?.email ? masterUserDataById.email : "",
            // password: masterUserDataById?.password
            //     ? masterUserDataById.password
            //     : "",
            profilePic: masterUserDataById?.profilePic
                ? masterUserDataById.profilePic
                : null,
            profilePicPreview: masterUserDataById?.profilePic
                ? `${MEDIA_URL}/qbotmasterapp.netcluesdemo.com/${profileFolderName}/${masterUserDataById.profilePic}`
                : "",
            isActive: masterUserDataById ? masterUserDataById.isActive : true,
            twoFactor: masterUserDataById ? masterUserDataById.twoFactor : true,
            roleId: masterUserDataById ? masterUserDataById.roleId : "",
            assignUserIds: masterUserDataById?.assignUserIds
                ? masterUserDataById.assignUserIds.toString()
                : "",

        },
        validationSchema: masterUserDataById ? validationUpdateSchema : validationCreateSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setButtonDisable(true);
            setLoading(true);

            try {
                const formData = new FormData();

                formData.append("firstName", values.firstName);
                formData.append("lastName", values.lastName);
                formData.append("email", values.email);
                formData.append("twoFactor", values.twoFactor);
                formData.append("roleId", values.roleId);
                formData.append("assignUserIds", values.assignUserIds);

                if (masterUserDataById) {
                    formData.append("isActive", values.isActive);
                    formData.append("filename", masterUserDataById.profilePic);
                    formData.append("isAdmin", masterUserDataById.isAdmin);
                }

                if (values.profilePic) {
                    formData.append("profilePic", values.profilePic);
                }
                formData.append("isImageEdited", isImageEdited);

                if (imageRemoved) {
                    formData.append("imageRemoved", imageRemoved);
                    formData.append("isImageEdited", false);
                }
                // formData.append("mediaUpload", true);
                if (masterUserDataById) {
                    const res = await editUserAction(formData, masterUserDataById.id);
                    setSnackbar({
                        open: true,
                        message: res?.data?.message,
                        severity: "success",
                    });
                    setShouldFetchData(1);
                } else {
                    const res = await createUserAction(formData);
                    // if (res.status === "202") {
                    //     setSnackbar({
                    //         open: true,
                    //         message: res?.data?.message,
                    //         severity: "info",
                    //     });
                    // }
                    // if (res.status === "200") {
                    //     setSnackbar({
                    //         open: true,
                    //         message: res?.data?.message,
                    //         severity: "success",
                    //     });
                    // }
                    setSnackbar({
                        open: true,
                        message: res?.data?.message,
                        severity: "success",
                    });
                    setShouldFetchData(1);
                }

                // Reset states and close modal only on success
                setIsImageEdited(false);
                setImageRemoved(false);
                formik.resetForm();
                handleClose();

            } catch (error) {
                console.error(error, "error")
                const message = error.message ?? error.error
                setSnackbar({
                    open: true,
                    message,
                    severity: "error",
                });
            } finally {
                setLoading(false);
                setButtonDisable(false);
            }
        },
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!open) {
            setIsImageEdited(false);
            setImageRemoved(false);
            formik.resetForm();
        }
    }, [open]);

    const handleMerchantChange = (event, newValue) => {
        const selectedIds = newValue.map((item) => item.id).join(","); // Join IDs as a comma-separated string
        formik.setFieldValue("assignUserIds", selectedIds); // Updates the merchantId as a comma-separated string
    };

    const handleFileSelect = (file) => {
        setIsImageEdited(true);
        setImageRemoved(false);
        formik.setFieldValue("profilePic", file);

        // Preview the selected image
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                formik.setFieldValue("profilePicPreview", e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePicture = () => {
        setIsImageEdited(true);
        setImageRemoved(true);
        formik.setFieldValue("profilePic", null);
        formik.setFieldValue("profilePicPreview", "");
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth>
            <DialogTitle className="card-header text-bg-primary fw-bold fs-2 ">
                {masterUserDataById ? "Update" : "Add"} User
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <label className="form-label">First Name<span className="text-danger ms-1">*</span></label>
                    <TextField
                        placeholder="Enter first name"
                        className="mt-0"
                        name="firstName"
                        size="small"
                        value={formik.values.firstName}
                        onChange={(e) => {
                            formik.setFieldValue('firstName', e.target.value.replace(/\s/g, ''));
                        }}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                        }
                        helperText={
                            formik.touched.firstName && formik.errors.firstName
                        }
                        fullWidth
                        margin="normal"
                    />
                    <label className="form-label">Last Name<span className="text-danger ms-1">*</span></label>
                    <TextField
                        placeholder="Enter last name"
                        className="mt-0"
                        size="small"
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={(e) => {
                            formik.setFieldValue('lastName', e.target.value.replace(/\s/g, ''));
                        }}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                        }
                        helperText={
                            formik.touched.lastName && formik.errors.lastName
                        }
                        fullWidth
                        margin="normal"
                    />
                    <label className="form-label">Email<span className="text-danger ms-1">*</span></label>
                    <TextField
                        placeholder="Enter email"
                        className="mt-0"
                        label=""
                        name="email"
                        size="small"
                        value={formik.values.email}
                        onChange={(e) => {
                            formik.setFieldValue('email', e.target.value.replace(/\s/g, ''));
                        }}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                        fullWidth
                        margin="normal"
                    />

                    {/* {!masterUserDataById && (
                        <>
                            <label className="form-label ">Password</label>
                            <TextField
                                className="mt-0"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                    formik.touched.password &&
                                    Boolean(formik.errors.password)
                                }
                                helperText={
                                    formik.touched.password &&
                                    formik.errors.password
                                }
                                fullWidth
                                size="small"
                                margin="normal"
                                disabled={masterUserDataById ? true : false}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment
                                            position="end"
                                            sx={{ gap: "10px" }}
                                        >
                                            <IconButton
                                                onClick={
                                                    handleTogglePasswordVisibility
                                                }
                                                edge="end"
                                            >
                                                {showPassword ? (
                                                    <MyVisibilityOffIcon />
                                                ) : (
                                                    <MyVisibilityIcon />
                                                )}
                                            </IconButton>
                                            <HtmlTooltip
                                                title={
                                                    <React.Fragment>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                    marginRight: 1,
                                                                }}
                                                            >
                                                                {"1. Length: "}
                                                                <Typography color="inherit">
                                                                    {
                                                                        "Password must be at least 8 characters long."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {
                                                                    "2. Complexity: "
                                                                }
                                                                <Typography color="inherit">
                                                                    {
                                                                        "Include a mix of uppercase and lowercase letters, numbers, and special characters (e.g., !, @, #, $)."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                color="inherit"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {
                                                                    "3. Regular Updates: "
                                                                }
                                                                <Typography color="inherit">
                                                                    {
                                                                        "It's recommended to change your password periodically for added security."
                                                                    }
                                                                </Typography>
                                                            </Typography>
                                                        </Box>
                                                    </React.Fragment>
                                                }
                                            >
                                                <IconButton>
                                                    <InfoIcon />
                                                </IconButton>
                                            </HtmlTooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </>
                    )} */}

                    <label className="form-label">Role<span className="text-danger ms-1">*</span></label>
                    <FormControl
                        fullWidth
                        size="small"
                        margin="normal"
                        className="mt-0"
                        error={
                            formik.touched.roleId &&
                            Boolean(formik.errors.roleId)
                        }
                    >
                        <Select
                            label="Role"
                            name="roleId"
                            value={formik.values.roleId}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            fullWidth
                            displayEmpty
                            input={<OutlinedInput />}
                            sx={SelectDropdown_SX}
                            error={
                                formik.touched.roleId &&
                                Boolean(formik.errors.roleId)
                            }

                        >
                            {/* Assuming you have a list of roles fetched from the backend */}
                            <MenuItem value="" disabled>
                                Select role
                            </MenuItem>
                            {roles.map((role) => (
                                <MenuItem key={role.id} value={role.id}>
                                    {role.roleName}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.roleId && formik.errors.roleId && (
                            <FormHelperText>
                                {formik.errors.roleId}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <label className="form-label">Tenant User<span className="text-danger ms-1">*</span></label>
                    <Autocomplete
                        multiple
                        size="small"
                        margin="normal"
                        options={
                            Array.isArray(merchantUser) ? merchantUser : []
                        }
                        getOptionLabel={(option) => {
                            return `${option.firstName} ${option.lastName}`;
                        }}
                        value={(() => {
                            if (
                                !formik.values.assignUserIds ||
                                !Array.isArray(merchantUser)
                            ) {
                                return [];
                            }

                            const orgIds =
                                formik.values.assignUserIds.toString();
                            const selectedIds = orgIds.includes(",")
                                ? orgIds
                                    .split(",")
                                    .filter((id) => id)
                                    .map((id) => id.trim())
                                : [orgIds].filter((id) => id);

                            return merchantUser.filter((option) =>
                                selectedIds.includes(option.id.toString()),
                            );
                        })()}
                        onChange={handleMerchantChange}
                        onBlur={() => formik.setFieldTouched('assignUserIds', true)}
                        isOptionEqualToValue={(option, value) =>
                            option.id.toString() === value.id.toString()
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select tenant user"
                                error={
                                    formik.touched.assignUserIds &&
                                    Boolean(formik.errors.assignUserIds)
                                }
                                helperText={
                                    formik.touched.assignUserIds &&
                                    formik.errors.assignUserIds
                                }
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                {option.firstName} {option.lastName}
                            </li>
                        )}
                    />
                    {masterUserDataById && (
                        <div className="mt-5">
                            <label>Active</label>
                            <CustomSwitch
                                name="isActive"
                                // checked={formik.values.isActive}
                                checked={Boolean(formik.values.isActive)}
                                onChange={(event) => {
                                    formik.setFieldValue(
                                        "isActive",
                                        event.target.checked,
                                    );
                                }}
                            />
                        </div>
                    )}

                    {masterUserDataById && (
                        <div className="mt-5">
                            <label>Two-Factor Authentication</label>
                            <CustomSwitch
                                name="twoFactor"
                                checked={Boolean(formik.values.twoFactor)}
                                // checked={formik.values.twoFactor}
                                onChange={(event) => {
                                    formik.setFieldValue(
                                        "twoFactor",
                                        event.target.checked,
                                    );
                                }}
                            />
                        </div>
                    )}

                    <div className="mt-5">
                        <ProfilePictureUpload
                            onFileSelect={handleFileSelect}
                            onRemove={handleRemovePicture}
                            accept="image/jpeg,image/png,image/jpg"
                            maxSize={2 * 1024 * 1024} // 2MB
                            error={
                                formik.touched.profilePic &&
                                    formik.errors.profilePic
                                    ? formik.errors.profilePic
                                    : null
                            }
                            preview={formik.values.profilePicPreview}
                            firstName={formik.values.firstName || ""}
                            lastName={formik.values.lastName || ""}
                            size={120}
                        />
                    </div>
                </DialogContent>
                <DialogActions className=" border-top p-7 d-flex justify-content-between">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="btn btn-sm btn-outline btn-outline-primary"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        className="btn btn-sm btn-primary"
                        disabled={!formik.isValid || buttonDisable || loading}
                    >
                        {masterUserDataById ? "Update" : "Add"} User
                        {loading ? (
                            <span
                                className="spinner-border spinner-border-sm ms-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        ) : null}
                    </button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddEditMasterUserModal;
